import { useState, useEffect, useContext } from "react"
import { GlobalVars } from "../../../state/global/globalVars";
import { eventIcons } from "../../../state/schedule/eventIcons";
import { APIStatusContext } from "../../../providers/apiStatusProvider";
import { useAuth } from "../../../providers/AuthProvider";
import Button from "../../ui/form/Button";
import { handleSchedulingApiError } from "../../../utils/errorHandler";

import UilEdit from "@iconscout/react-unicons/icons/uil-edit";
import UilTruck from "@iconscout/react-unicons/icons/uil-truck";
import UilBrowser from "@iconscout/react-unicons/icons/uil-browser";
import UilReceipt from "@iconscout/react-unicons/icons/uil-receipt";
import UilTrashAlt from "@iconscout/react-unicons/icons/uil-trash-alt";

import ModalWrapper from "../../ui/content/ModalWrapper";
import DetailView from "../../ui/content/DetailView";
import { MessageContext } from "../../../providers/MessageProvider";


import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);

const defaultTz = "America/Los_Angeles";

export default function EventDetail({eventId, closeEventModal, openEventEditModal }) {
    const [event, setEvent] = useState();
    const [formRowData, setFormRowData] = useState();
    const [showEnRouteButton, setShowEnRouteButton] = useState(true);
    const [showCOSButton, setShowCOSButton] = useState(true);
    // const [showJiraTicketButton, setShowJiraTicketButton] = useState(true);
    const [showKairosTicketButton, setShowKairosTicketButton] = useState(true);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { showMessage } = useContext(MessageContext);
    const { account, tokenCache } = useAuth();

    const { setIsLoading } = useContext(APIStatusContext);
    const { isLoading } = useState(false);

    useEffect(() => {
        getEvent(eventId);
        setShowDeleteConfirm(false);
    }, [eventId]);

    useEffect(() => {
        if (isLoading) {
            setIsLoading(true);
        }

        return () => {
            setIsLoading(false);
        }
    }, [isLoading]);

    async function getEvent(eventId) {
        const req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event/${eventId}`, {
            method: 'GET',
            credentials: 'include' // Include credentials in cross-origin requests
        });

        // Handle errors consistently
        const errorHandled = await handleSchedulingApiError(req, showMessage);
        if (errorHandled) {
            return; // Stop execution if error was handled
        }

        const res = await req.json();

        let installerNames = []
        for (let i = 0, len = res.Installers.length; i < len; i++) {
            let installer = res.Installers[i].User;
            installerNames.push(`${installer.firstName} ${installer.lastName}`);
        }

        // TIMEZOOOOOOOOOOOOOOOOOOONES
        // Set whatever from ISO string to LA tz
        // Probaby should clean this up at a later point
        const dayjsStr = dayjs(res.datetimeStart);
        const losAngelesTZ = dayjsStr.tz(defaultTz);

        let type = res.ScheduleEventType.name;
        if (res.subEvent) {
            type = type + ` (${res.subEvent})`;
        }

        setEvent(res);

        // Make sure the rows stays in order
        // Gross, I know
        let rowData = [
            {
                label: "Work Order Task",
                content: <div className="flex items-center gap-1">{eventIcons[res.ScheduleEventType.icon]?.icon} {type}</div>
            },
            {
                label: "Installers",
                content: installerNames.join(", ")
            }
        ]

        if (res.customerName) {
            rowData.push(
                {
                    label: "Customer Name",
                    content: `${res.customerName}`
                },
            )
        }

        if (res.customerPhone) {
            rowData.push(
                {
                    label: "Customer Phone",
                    content: `${res.customerPhone}`
                },
            )
        }

        if (res.address) {
            rowData.push(
                {
                    label: "Address",
                    content: `${res.address}`
                },
            )
        }

        rowData.push(
            {
                label: "Date",
                content: `${losAngelesTZ.format("ddd MMMM D, YYYY")}`
            },
            {
                label: "Time",
                content: `${dayjs(res.datetimeStart).tz(defaultTz).format("h:mm A")} - ${dayjs(res.datetimeEnd).tz(defaultTz).format("h:mm A")}`
            },
            {
                label: "Status",
                content: `${res.ScheduleEventStatus.name}`
            }
        )

        if (res.cosWorkOrderNumber) {
            rowData.push(
                {
                    label: "COS Work Order #",
                    content: `${res.cosWorkOrderNumber}`
                }
            )
        } else {
            setShowCOSButton(false);
            setShowEnRouteButton(false);
        }

        // if (res.jiraTicketId) {
        //     rowData.push(
        //         {
        //             label: "Jira Ticket #",
        //             content: `${res.jiraTicketId}`
        //         }
        //     )
        // } else {
        //     setShowJiraTicketButton(false);
        // }

        if (res.hermesTicketId) {
            rowData.push(
                {
                    label: "Kairos Ticket #",
                    content: `${res.hermesTicketId}`
                }
            )
        } else {
            setShowKairosTicketButton(false);
        }

        if (res.failedNotes) {
            rowData.push(
                {
                    label: "Needs Action Notes",
                    content: <div className="whitespace-pre-line">{res.failedNotes}</div>
                },
            )
        }

        rowData.push(
            {
                label: "Notes",
                content: <div className="whitespace-pre-line">{res.notes}</div>
            },
            {
                label: "Created",
                content: `${dayjs(res.createdAt).format("MMMM D, YYYY | h:mm A")}`
            },
            {
                label: "Last Updated",
                content: `${dayjs(res.updatedAt).format("MMMM D, YYYY | h:mm A")}`
            }
        )

        setFormRowData(rowData);
    }

    async function setEnRoute(event) {
        setIsLoading(true);

        let req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event/${event.id}/enroute`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include' // Include credentials in cross-origin requests
        });

        // Handle errors consistently
        const errorHandled = await handleSchedulingApiError(req, showMessage);
        if (errorHandled) {
            setIsLoading(false);
            return;
        }

        closeEventModal();
        setIsLoading(false);
    }

    async function deleteEvent(event) {
        setIsLoading(true);

        const requestBody = {
            account: account,
            tokenCache: tokenCache
        };

        let req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event/${event.id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include', // Include credentials in cross-origin requests
            body: JSON.stringify(requestBody)
        });

        // Handle errors consistently
        const errorHandled = await handleSchedulingApiError(req, showMessage);
        if (errorHandled) {
            setIsLoading(false);
            return;
        }

        closeEventModal();
        setIsLoading(false);
    }


    function openWorkOrderCOSWindow(event) {
        window.open(`https://citysidefiber.cosadmin.net/EntityObjectDeploymentWorkOrder/Show/${event.cosWorkOrderId}`, "_blank");
    }


    // function openJiraTicketWindow(event) {
    //     window.open(`https://citysidefiber.atlassian.net/browse/${event.jiraTicketId}`, "_blank");
    // }

    function openKairosTicketWindow(event) {
        window.open(`http://crm.citysidefiber.com:667/?page=ticketOverview&ticketId=${event.hermesTicketId}`, "_blank");
    }


    return (
        <ModalWrapper
            title="Work Order Task Detail"
            onClose={closeEventModal}
            footerContent={
                <div className="w-auto flex flex-col sm:flex-row gap-2">
                    <div className="flex gap-2">
                        <Button
                            text="Edit"
                            className="w-full sm:w-32"
                            icon={<UilEdit size={GlobalVars.iconSize.xs} />}
                            onClick={() => openEventEditModal(event)}
                        />
                        {showEnRouteButton &&
                        <Button
                            text="Set En Route"
                            className="w-full sm:w-32"
                            icon={<UilTruck size={GlobalVars.iconSize.xs} />}
                            onClick={() => setEnRoute(event)}
                        />
                        }
                        {showCOSButton &&
                            <Button
                                text="View in COS"
                                className="w-full sm:w-32"
                                icon={<UilBrowser size={GlobalVars.iconSize.xs} />}
                                onClick={() => openWorkOrderCOSWindow(event)}
                            />
                        }
                        {/* {showJiraTicketButton &&
                            <Button
                                text="View in Jira"
                                className="w-full sm:w-32"
                                icon={<UilReceipt size={GlobalVars.iconSize.xs} />}
                                onClick={() => openJiraTicketWindow(event)}
                            />
                        } */}
                        {showKairosTicketButton &&
                            <Button
                                text="View in Kairos"
                                className="w-full sm:w-32"
                                icon={<UilReceipt size={GlobalVars.iconSize.xs} />}
                                onClick={() => openKairosTicketWindow(event)}
                            />
                        }
                        <Button
                            text="Delete"
                            className="w-full sm:w-32"
                            icon={<UilTrashAlt size={GlobalVars.iconSize.xs} />}
                            onClick={() => showDeleteConfirm ? deleteEvent(event) : setShowDeleteConfirm(true)}
                        />
                    </div>
                </div>
            }
        >
            { event && (
                <DetailView
                    className="grid grid-cols-3 bg-light-11 overflow-auto"
                    labelClass="text-right bg-light-10 font-semibold text-md border-b-light-10 border-b pr-3 py-1"
                    contentClass="col-span-2 border-b-light-9 border-b py-1 pl-3"
                    rowData = {formRowData}
                />
            )}
            {showDeleteConfirm && (
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative m-3" role="alert">
                    <strong className="font-bold">⚠️ Are you sure you want to delete this event?&nbsp;</strong>
                    <span className="block sm:inline">Deleting this event will remove it from the calendar and remove any respective scheduled tasks in COS. If you are sure you would like to delete this event, click on the &ldquo;Delete&rdquo; button again.</span>
                </div>
            )}
        </ModalWrapper>
    )
}